import {
    DefaultError,
    InfiniteData,
    keepPreviousData,
    useInfiniteQuery,
} from '@tanstack/react-query';
import {
    DataSource,
    DataSourceOptions,
    DataSourcePaging,
    DataSourceQueryResult,
    firstPage,
    ItemKeyCallback,
    replaceItems,
} from 'custom_hooks/data_hooks/data_source/data_source';
import { useCallback, useMemo, useRef, useState } from 'react';
import { queryClient } from 'utils/query';

import { useDataSourceParams } from './useDataSourceParams';

export const defaultInfiniteDataSourcePageSize = 100;

/**
 *
 * @param data The query result data to update.
 * @param items Updated item list.
 * @param itemKey Callback used to uniquely identify an item (used for replacing items).
 * @returns The updated query result data.
 */
export function replaceItemsInInfiniteResult<TData>(
    data: InfiniteData<DataSourceQueryResult<TData>> | undefined,
    items: TData[],
    itemKey: ItemKeyCallback<TData>
): InfiniteData<DataSourceQueryResult<TData>> | undefined {
    return items.length > 0 &&
        data &&
        data.pages.length > 0 &&
        data.pages[0].results.length > 0
        ? {
              ...data,
              pages: data.pages.map<DataSourceQueryResult<TData>>((page) => ({
                  ...page,
                  results: replaceItems<TData>(page.results, items, itemKey),
              })),
          }
        : data;
}

export type InfiniteDataSource<TData, TFilter> = DataSource<TData, TFilter> &
    Readonly<{
        /**
         * Indicates if another page is available.
         */
        hasNext: boolean | undefined;

        /**
         * Loads the next page of data.
         */
        loadNext: () => Promise<void>;

        /**
         * Refetch data
         */
        refetch: () => Promise<unknown>;
    }>;

/**
 * Initializes an infinite data source.
 *
 * @param param0 Configuration options.
 */
export function useInfiniteDataSource<TData, TFilter>({
    name,
    enabled = true,
    keepPreviousData: shouldKeepPreviousData = false,
    defaultFilter,
    defaultSort = [],
    pageLimit = defaultInfiniteDataSourcePageSize,
    query,
    retry,
    refetchInterval,
    staleTime,
}: DataSourceOptions<TData, TFilter>): InfiniteDataSource<TData, TFilter> {
    const defaultFilterRef = useRef(defaultFilter);
    defaultFilterRef.current = defaultFilter;
    const defaultSortRef = useRef(defaultSort);
    defaultSortRef.current = defaultSort;

    const [filter, setFilter] = useState(defaultFilter);
    const [sort, setSort] = useState(defaultSort);

    // Use the shared hook for filter parameter updates
    useDataSourceParams(defaultFilter, setFilter, name);

    const {
        isFetching,
        data,
        error,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useInfiniteQuery<
        DataSourceQueryResult<TData>,
        DefaultError,
        InfiniteData<DataSourceQueryResult<TData>>,
        readonly unknown[],
        number
    >({
        queryKey: [name, filter, sort, pageLimit],
        queryFn: async ({ pageParam = firstPage }) =>
            query({
                filter,
                sort,
                page: pageParam,
                pageLimit,
            }),
        initialPageParam: 1,
        getNextPageParam: ({ currentPage, totalPages }) =>
            currentPage < totalPages ? currentPage + 1 : undefined,
        enabled,
        placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
        retry,
        refetchInterval,
        staleTime,
    });

    const results = useMemo(() => data?.pages.flatMap((page) => page.results), [data]);

    const paging = useMemo<DataSourcePaging>(() => {
        const lastPage = data ? data.pages[data.pages.length - 1] : undefined;
        return {
            page: data?.pages.length ?? firstPage,
            pageLimit,
            totalPages: lastPage?.totalPages,
            totalResults: lastPage?.totalResults,
        };
    }, [data, pageLimit]);

    const reset = useCallback(() => {
        queryClient.resetQueries({
            queryKey: [name],
        });
        setFilter(defaultFilterRef.current);
        setSort(defaultSortRef.current);
    }, [name]);

    const loadNext = useCallback<() => Promise<void>>(async () => {
        await fetchNextPage();
    }, [fetchNextPage]);

    const updateData = useCallback(
        (items: TData[], itemKey: ItemKeyCallback<TData>) => {
            queryClient.setQueryData<
                InfiniteData<DataSourceQueryResult<TData>> | undefined
            >([name, filter, sort, pageLimit], (current) =>
                replaceItemsInInfiniteResult(current, items, itemKey)
            );
        },
        [filter, name, pageLimit, sort]
    );

    return {
        filter,
        sort,
        isLoading: isFetching,
        error,
        paging,
        results,
        hasNext: hasNextPage,
        setFilter,
        setSort,
        loadNext,
        reset,
        refetch,
        updateData,
    };
}
